/** 
 * @namespace AlertDialog
 * @memberof Promega.Shell
 * @description The AlertDialog is a large, full-width banner that can be displayed at the top of the site above all other content.
 * Examples include the country message that is shown on first visit, as well as the "Testing Environment" message that can be seen in non-production environments such as [test.promega.com]{@link https://test.promega.com}.
 * Instances of the AlertDialog are typically created and populated within Sitecore: the methods provided here only enable the showing or hiding of an AlertDialog that is already present in the page markup.
 * 
 * @example <caption>An alert dialog is typically shown at the top of the page. It must have class <code>alert-container</code>.<br><br>
 * The alert dialog spans the width of the browser window by default, so you will typically want to include a content-width container
 * within it:</caption>
 * <div class="alert-container">
 *   <div class="alert-container__content content-width">Alert dialog content goes here.</div>
 * </div>
 * 
 * @example <caption>If you include a <code>&lt;button&gt;</code> element with class <code>alert-container__dismiss-button</code>,
 * then clicking this button will cause the alert dialog to be dismissed:</caption>
 * <div class="alert-container">
 *   <div class="alert-container__content content-width">
 *     <p>Alert dialog content goes here.</p>
 *     <button type="button" class="button button--light-gray alert-container__dismiss-button</button>
 *   </div>
 * </div>
 * 
 * @example <caption>Backgrounds are also supported:</caption>
 * <div class="alert-container has-background">
 *   <div class="background-element background--gray-50"></div>
 *   <div class="alert-container__content content-width">Alert dialog content goes here.</div>
 * </div>
 * 
 * @example <caption>You can set attribute <code>data-autoshow="true"</code> to have the alert dialog automatically be shown on page load:</caption>
 * <div class="alert-container" data-autoshow="true">
 *   <div class="alert-container__content content-width">Alert dialog content goes here.</div>
 * </div>
 * 
 * @example <caption> You can also specify the name of a cookie to set in attribute <code>data-set-cookie-on-hide</code> in order to have a cookie
 * set when the user dismisses the dialog. Overrides attribute <code>data-autoshow</code>. Useful for making sure an automatic dialog stays
 * dismissed on subsequent page loads.<br><br>
 * On subsequent pages loads, if the alert dialog has a configuration of <code>data-set-cookie-on-hide</code> that matches a cookie,
 * the dialog will not be shown even if <code>data-autoshow="true"</code> is set. You can still manually show the dialog by calling
 * <code>Promega.Shell.AlertDialog.showDialog(elementContainerGoesHere)</code> in JavaScript.
 * <div class="alert-container" data-set-cookie-on-hide='{"name": "myCookieName", "value": "The value to set for the cookie"}'>
 *   <div class="alert-container__content content-width">Alert dialog content goes here.</div>
 * </div>
 */

import isAnimationSupported from '../util/isAnimationSupported';
import trackEvent from '../util/trackEvent';
import cookie from 'cookie_js';

const ALERT_CONTAINER_CLASS = "alert-container",
    ALERT_CONTAINER_VISIBLE_CLASS = "alert-container--visible",
    ALERT_ACCORDION_TITLE_CLASS = "alert-accordion-title",
    ALERT_ACCORDION_PANEL = "alert-accordion-panel",
    DISMISS_BUTTON_CLASS = "alert-container__dismiss-button",
    ACCEPTALL_BUTTON_CLASS = "alert-container__acceptall-button";
    /* SELECTCOOKIES_BUTTON_CLASS = "alert-container__selectcookies-button"; */

// Private functions/listeners

function toggleDetails(toggleEl, options) {
    if (!toggleEl) return null;
    options = options || {};
    const container = toggleEl.closest('.alert-section');
    const panel = container && container.querySelector('.alert-accordion-panel');
    if (!panel) return null;

    const isExpanded = toggleEl.getAttribute('aria-expanded') === 'true';
    const shouldExpand = typeof options.force === 'boolean' ? options.force : !isExpanded;

    toggleEl.setAttribute('aria-expanded', shouldExpand ? 'true' : 'false');
    panel.setAttribute('aria-hidden', shouldExpand ? 'false' : 'true');

    // read server-localized strings (fallbacks provided)
    const viewText = toggleEl.dataset.viewText || toggleEl.getAttribute('data-view-text') || 'View details';
    const hideText = toggleEl.dataset.hideText || toggleEl.getAttribute('data-hide-text') || 'Hide details';

    // If your toggle button contains only text, you can set textContent.
    // If it contains an icon + text, consider targeting a dedicated label element.
    const labelNode = toggleEl.querySelector('.toggle-label') || toggleEl;
    labelNode.textContent = shouldExpand ? hideText : viewText;

    // optional: return state for callers/tests
    return { container, panel, toggleEl, isOpen: shouldExpand };
}

function dismissAlert(buttonEl) {
    if (!buttonEl) return;

    // find the logical alert section (accept .alert-container, .alert-section, or fallback to nearest section)
    var alertSection = buttonEl.closest('.alert-container') || buttonEl.closest('.alert-section') || buttonEl.closest('section');
    if (!alertSection) return;

    // set the section hidden (do not remove)
    // use HTML hidden attribute which is semantic and clears it from sequential reading/layout
    alertSection.hidden = true;

    // set cookie if the data attribute exists (copy of provided logic, scoped to this section)
    try {
        var raw = alertSection.getAttribute('data-set-cookie-on-hide');
        if (!raw) return;
        // attempt parse (handles encoded quotes)
        var parsed = JSON.parse(raw.replace(/&quot;/g, '"'));
        var name = parsed.name;
        var value = parsed.value || 'true';
        var expires = parsed.expires || 365; // days

        var d = new Date();
        d.setTime(d.getTime() + expires * 24 * 60 * 60 * 1000);
        document.cookie = name + '=' + encodeURIComponent(value) + ';path=/;expires=' + d.toUTCString();
    } catch (e) {
        // ignore parse/set errors but log for visibility
        // (keeps behavior resilient if attribute is malformed)
        console.warn('Unable to set cookie for alert hide', e);
    }
}


function addAlertDialogMobileExpand (event) {
    if (event.target && event.target.classList.contains(ALERT_ACCORDION_TITLE_CLASS)) {
        const accordionTitle = event.target;
        const accordionPanel = event.target.nextElementSibling;

        accordionTitle.classList.toggle("alert-accordion-title-expand");

        if (accordionPanel.style.maxHeight) {
            accordionPanel.style.maxHeight = null;
        } else {
            accordionPanel.style.maxHeight = accordionPanel.scrollHeight + "px";
        }

    }
}

function alertDialogClicked (event) {
    if (!event || !event.target) return;

    // handle dismiss (works if the click target is the svg/span inside the button)
    const dismissBtn = event.target.closest(`.${DISMISS_BUTTON_CLASS}`);
    if (dismissBtn) {
        // try to find the container from the button itself
        const containerParent = dismissBtn.closest(`.${ALERT_CONTAINER_CLASS}`) || dismissBtn.closest('.alert-section') || dismissBtn.closest('section');
        if (containerParent) {
            hideDialog(containerParent);
            trackEvent("Components", "Alert dialog - dismissed");
        }
        return; // handled
    }

    // handle accept-all (cookie) similarly
    const acceptBtn = event.target.closest(`.${ACCEPTALL_BUTTON_CLASS}`);
    if (acceptBtn) {
        const containerParent = acceptBtn.closest(`.${ALERT_CONTAINER_CLASS}`) || acceptBtn.closest('.alert-section') || acceptBtn.closest('section');
        if (containerParent) {
            hideDialog(containerParent);
            trackEvent("Components", "Alert dialog - acceptall");
        }
        return; // handled
    }
    /*
    if (event.target && event.target.classList.contains(SELECTCOOKIES_BUTTON_CLASS)) {
        alert('This will show the Select Cookies dialog');
        trackEvent("Components", "Alert dialog - selectcookies");
    }
    */
}

function hideAlertDialogAfterTransition(event) {
    const containerEl = event.target;
    if (containerEl.classList.contains(ALERT_CONTAINER_CLASS)) {
        containerEl.classList.remove(ALERT_CONTAINER_VISIBLE_CLASS);
        containerEl.style.transition = "";
        containerEl.style.marginBottom = "";
        containerEl.removeEventListener("transitionend", hideAlertDialogAfterTransition);
    }
}

// API

/**
 * @function showDialog
 * @param {HTMLElement} containerEl The AlertDialog to show.
 * @memberof Promega.Shell.AlertDialog
 * @description Shows the given AlertDialog.
 */
export function showDialog(containerEl) {
    if (containerEl && containerEl.classList.contains(ALERT_CONTAINER_CLASS) && !containerEl.classList.contains(ALERT_CONTAINER_VISIBLE_CLASS)) {
        containerEl.classList.add(ALERT_CONTAINER_VISIBLE_CLASS);

    }
}

/**
 * @function hideDialog
 * @param {HTMLElement} containerEl The AlertDialog to hide.
 * @memberof Promega.Shell.AlertDialog
 * @description Hides the given AlertDialog.
 */
export function hideDialog (containerEl) {
    if (containerEl && containerEl.classList.contains(ALERT_CONTAINER_CLASS) && containerEl.classList.contains(ALERT_CONTAINER_VISIBLE_CLASS)) {
        if (isAnimationSupported()) {
            const boundingRect = containerEl.getBoundingClientRect();
            containerEl.style.transition = "margin-bottom .3s ease";
            containerEl.addEventListener("transitionend", hideAlertDialogAfterTransition);
            containerEl.style.marginBottom = `-${boundingRect.height}px`;
        }
        else hideAlertDialogAfterTransition({ target: containerEl });

        let setCookieOnHide = containerEl.getAttribute("data-set-cookie-on-hide");
        if (setCookieOnHide) {
            setCookieOnHide = JSON.parse(setCookieOnHide);
            if (setCookieOnHide.maxAge) {
                cookie.set(setCookieOnHide.name, setCookieOnHide.value, { "max-age": setCookieOnHide.maxAge, "path": "/" });
                console.warn("Cookies max-age option does not appear to be supported any longer (03/02/2021 CPAULY)");
            }
            else if (setCookieOnHide.expires) {
                cookie.set(setCookieOnHide.name, setCookieOnHide.value, { "expires": setCookieOnHide.expires, "path": "/" });
            }
            else {
                cookie.set(setCookieOnHide.name, setCookieOnHide.value, { path: "/" });
            }
        }
    }
}

// Init code

if (typeof document !== "undefined") {
    const allAlertDialogs = document.getElementsByClassName(ALERT_CONTAINER_CLASS);
    for (let i = 0; i < allAlertDialogs.length; i++) {
        let setCookieOnHide = allAlertDialogs[i].getAttribute("data-set-cookie-on-hide"),
            autoshow = allAlertDialogs[i].getAttribute("data-autoshow");

        if (autoshow) {
            // Automatically show the dialog, unless the cookie that is set on hide is already set
            let alreadyHidden = false;
            if (setCookieOnHide) {
                setCookieOnHide = JSON.parse(setCookieOnHide);
                if (cookie.get(setCookieOnHide.name) === setCookieOnHide.value) {
                    alreadyHidden = true;
                }
            }
            if (!alreadyHidden) showDialog(allAlertDialogs[i]);
        }
    }

    document.addEventListener("click", addAlertDialogMobileExpand);
    document.addEventListener("click", alertDialogClicked);

    document.addEventListener('click', function (e) {
        const t = e.target;
        if (!t) return;

        // dismiss anywhere inside the button (svg/span clicks)
        const dismissBtn = t.closest('.alert-container__dismiss-button');
        if (dismissBtn) {
            e.preventDefault();
            dismissAlert(dismissBtn);
            return;
        }

        // toggle via the explicit button or the title element
        const toggleBtn = t.closest('.alert-toggle-details');
        const titleBtn = t.closest('.alert-accordion-title');
        const trigger = toggleBtn || titleBtn;
        if (trigger) {
            e.preventDefault();
            toggleDetails(trigger);
            return;
        }
    });
}
